
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { IconSet, IconSetUnawareIcon } from "@/types/icons";
import { icomoonSvgDefsToIcons, svgToIcon } from "@/lib/icons/parser";
import { configStore } from "@/store/store";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import FileUpload from "@/components/Reusable/CustomInputs/FileUpload.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import { uploadIcons } from "@/lib/icons/ajax";

@Component({
  components: { FileUpload }
})
export default class IconUpload extends Vue {
  iconSetToSaveIconsTo: IconSet["slug"] | undefined = "";

  uploadedIcons: IconSetUnawareIcon[] = [];
  saveInProgress = false;

  chooseIcon(icon: IconSetUnawareIcon) {
    this.$emit("chooseIcon", icon);
  }

  handleFiles(files: FileList) {
    Array.from(files).forEach(file => {
      const reader = new FileReader();

      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const classThis = this;

      reader.onloadend = function () {
        if (typeof this.result !== "string") return;

        if (file.name === "symbol-defs.svg") {
          classThis.handleIcoMoonFile(this.result);
        } else if (file.type === "image/svg+xml") {
          classThis.handleSingleSvgFile(this.result, file.name);
        } else if (file.type === "application/zip") {
          NotificationLogger.log.message(
            "Please extract the contents of the zip file and upload the individual SVG files."
          );
        } else {
          NotificationLogger.log.message(
            "Upload symbol-defs.svg from an IcoMoon export, or an individual SVG file."
          );
        }
      };

      reader.readAsText(file);
    });
  }

  handleIcoMoonFile(contentsOfSymbolDefsDotSvg: string) {
    pipe(
      icomoonSvgDefsToIcons(contentsOfSymbolDefsDotSvg),
      fold(
        errors => errors.forEach(NotificationLogger.log.message),
        icons => this.uploadedIcons.push(...icons)
      )
    );
  }

  handleSingleSvgFile(singleSvgCode: string, filename: string) {
    pipe(
      svgToIcon(singleSvgCode, filename),
      fold(
        errors => errors.forEach(NotificationLogger.log.message),
        icon => this.uploadedIcons.push(icon)
      )
    );
  }

  async saveIcons() {
    if (typeof this.iconSetToSaveIconsTo !== "string") return;
    if (this.uploadedIcons.length < 1) {
      NotificationLogger.log.message("Upload some icons.");
      return;
    }

    /* we call setTimeout because the v-combox is a piece of shit
    and doesnt update its data until blur. most workarounds are bad:
    https://github.com/vuetifyjs/vuetify/issues/4679
    but if we just wait 100 milliseconds, blur is fired before we actually save the shit
    */

    this.saveInProgress = true;

    setTimeout(async () => {
      if (typeof this.iconSetToSaveIconsTo !== "string") return;

      this.saveInProgress = true;

      const iconSet: IconSet =
        this.iconSetToSaveIconsTo === ""
          ? { slug: "uncategorized", name: "Uncategorized", custom: false }
          : configStore.iconSets.find(
              configStoreIconSet =>
                configStoreIconSet.slug === this.iconSetToSaveIconsTo
            ) ?? {
              slug: this.iconSetToSaveIconsTo,
              name: this.iconSetToSaveIconsTo,
              custom: false
            };

      await uploadIcons(this.uploadedIcons, iconSet);

      this.saveInProgress = false;
      this.uploadedIcons = [];
      this.iconSetToSaveIconsTo = "";

      this.$emit("done", iconSet.slug);
    }, 100);
  }

  get iconSetsForDropdown() {
    return configStore.iconSets.map(iconSet => {
      return {
        text: iconSet.name,
        value: iconSet.slug,
        disabled: !iconSet.custom
      };
    });
  }
}
